import { Container } from 'react-bootstrap';
import PageSectionTitle from '../Layout/PageSectionTitle';
import PageSubsectionTitle from '../Layout/PageSubsectionTitle';
import MathJax from 'react-mathjax';
import "./FaqTextContent.css";

const FaqTextContent = () => {
  return(
    <div id="faq-text-content">
      <Container fluid>
        <MathJax.Provider>
        <span>
          Have a question and can't find an answer below? Contact us at <a href="mailto:acic2022@mathematica.org">
            acic2022@mathematica.org</a>. We'll post your question and our answer here.
        </span>
        <PageSectionTitle>
          Questions
        </PageSectionTitle>

        <PageSubsectionTitle>
          Will you accept submissions after the May 1st deadline?
        </PageSubsectionTitle>
        <p className="answer">
        <span className="notification-text">We are no longer accepting new submissions.</span> We hope to see you at ACIC 2023! 
          If you have any questions or concerns, you may still contact us at <a href="mailto:acic2022@mathematica.org">acic2022@mathematica.org</a>.
        </p>

        <PageSubsectionTitle>
          How can I identify which observations are receiving the treatment when?
        </PageSubsectionTitle>
        <p className="answer">
          Patients and practices only receive the intervention in cases where <span className="var-text">Z</span>=1 
          and <span className="var-text">post</span>=1. In the data, the variable <span className="var-text">Z</span> is 
          a time-invariant practice-level indicator for being a member of the eventual treatment group, and thus is either 
          0 for all 4 years or 1 for all 4 years, for any given patient or practice. The variable <span 
          className="var-text">post</span> is a time-varying indicator for whether the intervention has begun, equal to 0 
          for Years 1&ndash;2 and equal to 1 for Years 3&ndash;4, in all practices and for all patients. Therefore, the interaction 
          of <span className="var-text">Z</span> and <span className="var-text">post</span> is what identifies actual receipt 
          of the intervention.
        </p>

        <PageSubsectionTitle>
          How is treatment status determined?
        </PageSubsectionTitle>
        <p className="answer">
          Treatment status is assigned at the practice level, such that all patients in the same practice will have the same 
          treatment status. Treatment status can be a function of practice-level covariates (<span 
          className="var-text">X</span>), patient-level covariates (<span className="var-text">V</span>), and pre-treatment outcomes 
          (<span className="var-text">Y</span> in years 1 and 2).
        </p>

        <PageSubsectionTitle>
          If both patient- and practice-level covariates are time-invariant, how can the outcome vary over time?
        </PageSubsectionTitle>
        <p className="answer">
          There are two reasons the outcome can change over time even though the covariates do not; first, there could be stochastic 
          variation; second, the response surface itself may change over time. Also, please note that the practice-level averages of 
          patient-level covariates, <span className="var-text">V1_avg</span> &ndash; <span className="var-text">V5_C_avg</span>, do 
          vary over time. This is because they reflect the average of the patient-level covariates for the patients who are present 
          in the practice for that year.
        </p>

        <PageSubsectionTitle>
          What is the causal structure of the data?
        </PageSubsectionTitle>
        <p className="answer">
          In the real-world applications that this challenge is based on, we do not know the true underlying causal structure, and so 
          we must make assumptions and/or use flexible methods. We view that as part of the difficulty of the competition. That said, 
          we do rule out unobserved confounding; that is, there is no unobserved variable <span className="var-text">U</span> that 
          affects both <span className="var-text">Y</span> and <span className="var-text">Z</span>.
        </p>

        <PageSubsectionTitle>
          The ignorability assumption for identifying causal effects for the patients present in Years 3 and 4 requires pre-treatment 
          versions of the outcome variable. But these values don't exist for all such patients. Can we assume that missingness of 
          pre-period outcomes depends only on pre-treatment observables? 
        </PageSubsectionTitle>
        <p className="answer">
          Yes, missingness of pre-period outcomes (i.e. joining Medicare) depends only on pre-treatment observables in our DGPs.
        </p>

        <PageSubsectionTitle>
          The ignorability assumption states that <MathJax.Node
            inline formula='Y_{it_1}(0) - Y_{it_0}(0) \perp z_i \mid x_i'
          />. What does <MathJax.Node
            inline formula='x_i'
          /> stand for in this equation?
        </PageSubsectionTitle>
        <p className="answer">
          In that assumption, <MathJax.Node
            inline formula='x_i'
          /> stands for all pre-treatment observables, so both the covariates <span
          className="var-text">X1-X9</span> and <span
          className="var-text">V1-V5</span>, as well as other observables such as pre-period outcomes or practice size.
        </p>

        <PageSubsectionTitle>
          What are the datatypes for each of the variables in the dataset? 
        </PageSubsectionTitle>
        <p className='multipart-answer'>
          Our continuous variables are <span className="var-text">
            Y, V1, V2, V4, X6, X7, X8, X9, V1_avg, V2_avg, V3_avg, V4_avg, V5_A_avg, V5_B_avg, V5_C_avg,
          </span> and <span className="var-text">n.patients</span>.
        </p>
        <p className='multipart-answer'>
          Our unordered categorical variables are <span 
          className="var-text">X2, X4,</span> and <span className="var-text">V5</span>. Each of them has only 3 values.
        </p>
        <p className='answer'>
          Our binary variables are <span className="var-text">V3, X1, X3,</span> and <span className="var-text">X5</span>.
        </p>

        <PageSubsectionTitle>
          Can participants tell which of the 3,400 datasets came from which of the 17 data generation processes (DGPs)? 
        </PageSubsectionTitle>
        <p className="answer">
          No, the relationship between datasets and DGPs has been purposefully obfuscated. In a real-world observational study, 
          it's often unclear whether there's measured (or unmeasured) confounding, and if so how severe it is. For example, we 
          might imagine that certain characteristics increase the likelihood of participation and are correlated with outcomes, 
          but we don't know which are the characteristics nor how strong the correlations are. Our DGPs do not include unmeasured 
          confounding, but counteracting a range of possible and unknown measured confounding scenarios is part of the challenge, 
          which is why we're not specifying which datasets share a common DGP.
        </p>

        <PageSubsectionTitle>
          What distinguishes the 17 DGPs and the 3,400 datasets?
        </PageSubsectionTitle>
        <p className="answer">
          The joint distribution of (<span 
          className="var-text">Y</span>, <span 
          className="var-text">Z</span>, <span 
          className="var-text">Post</span>, <span 
          className="var-text">X</span>, <span 
          className="var-text">V</span>) differs among the 17 DGPs. For example, 
          the DGPs may differ in the degree of confounding present. For each of the 17 DGPs, we created 200 datasets using different 
          random number generator seeds, yielding a total of 3,400 datasets. (This setup is analogous to previous competitions. See, 
          for example, Section 4.3 of <a href="https://doi.org/10.1214/18-STS667">Dorie et al., 2019.</a>)
        </p>

        <PageSubsectionTitle>
          What file formats do you accept?
        </PageSubsectionTitle>
        <p className="answer">
          Submissions must be in .csv format.
        </p>

        <PageSubsectionTitle>
          May I choose creative column names for my submission?
        </PageSubsectionTitle>
        <p className='multipart-answer'>
          We're all for creativity in estimation but not in column naming. The file containing your estimates of 
          the SATT overall, by year, and by subgroup must have column 
          names: 
        </p>
        <p className='multipart-answer'>
          <span className="var-text">dataset.num, variable, level, year, satt, lower90, upper90</span>
        </p>
        <p className='multipart-answer'>
          The file containing your estimates of the SATT by practice, if you are submitting one, must have column names:
        </p>
        <p className="answer">
          <span className="var-text">dataset.num, id.practice, satt, lower90, upper90</span>.
        </p>

        <PageSubsectionTitle>
          Is there a limit to the number of people who can participate as a team?
        </PageSubsectionTitle>
        <p className="answer">
          There is no limit. You are free to collaborate with as many people as you wish.
        </p>

        <PageSubsectionTitle>
          Are teams allowed to make multiple submissions?
        </PageSubsectionTitle>
        <p className="answer">
          Absolutely! We want to hear about all of your great ideas.
        </p>

        <PageSubsectionTitle>
          What information will be published after the competition is over?
        </PageSubsectionTitle>
        <p className="answer">
          After the competition has ended, we will post the hidden treatment effects that were baked into the data. At 
          the ACIC conference, we will also give a brief presentation on the competition results, and we will follow up 
          with a more detailed paper with full results and an analysis of which types of methods tended to do best in 
          which circumstances.
        </p>

        <PageSubsectionTitle>
          What do I get if I win?
        </PageSubsectionTitle>
        <span className="answer">
          Honor, glory, and bragging rights &mdash; until next year.
        </span>
        </MathJax.Provider>
      </Container>
    </div>
  )
}

export default FaqTextContent;
